import {useDeepMemo} from '@wandb/weave/common/state/hooks';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createSelector} from 'reselect';

import * as Generated from '../../../generated/graphql';
import {trackRunColor} from '../../../services/analytics/runColorEvents';
import {
  useBulkEditorProjectTagsQuery,
  useRunSelectorQuery,
} from '../../../state/graphql/runSelectorQuery';
import {useSelectedCounts} from '../../../state/graphql/selectedCountQuery';
import {useMemoizedSelector} from '../../../state/hooks';
import {useRunsQueryContext} from '../../../state/runs/hooks';
import * as RunHooks from '../../../state/runs/hooks';
import type * as CustomRunColorsTypes from '../../../state/views/customRunColors/types';
import type * as CustomRunNamesTypes from '../../../state/views/customRunNames/types';
import * as FilterActions from '../../../state/views/filter/actions';
import * as GroupSelectionsActions from '../../../state/views/groupSelections/actions';
import * as ViewHooks from '../../../state/views/hooks';
import * as RunSetActions from '../../../state/views/runSet/actions';
import {useRunSetProject} from '../../../state/views/runSet/hooks';
import type * as RunSetTypes from '../../../state/views/runSet/types';
import * as SortActions from '../../../state/views/sort/actions';
import * as TempSelectionsActions from '../../../state/views/tempSelections/actions';
import type * as TempSelectionsTypes from '../../../state/views/tempSelections/types';
import {getFiltersForQuery} from '../../../util/filterV2';
import {useRampFlagRunsTableResultsHiddenByFiltersBanner} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {getParsedRunColorGroupKeys} from '../../../util/runColorGroup';
import {
  type Config as TableSettings,
  EMPTY_CONFIG as EMPTY_TABLE_SETTINGS,
} from '../../../util/runfeed';
import * as Run from '../../../util/runs';
import type * as RunTypes from '../../../util/runTypes';
import * as TableCols from '../../../util/tablecols';
import {TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {usePublicQueueAccess} from '../../Launch/hooks';
import {type JobInfo, type LaunchDrawerJobRun} from '../../Launch/utilTypes';
import {type RunSelectorAnalyticsLocation} from '../RunSelectorAnalyticsLocationContext';
import {makeRunSetSelector} from '../runSetSelectors';
import {getRows} from '../util';
import {useCombinedFilter} from './useCombinedFilter';
import {useDefaultKeys} from './useDefaultKeys';
import {useFilteredKeys} from './useFilteredKeys';
import {
  hasPinnedColumnsForLeanQuery,
  useLeanGroupedRunsQuery as useLeanGroupedRunsQueryFlag,
} from './useLeanGroupedRunsQuery';
import {useRunSelectorRuns} from './useRunSelectorRuns';
import {useSearchResultsHiddenByFilters} from './useSearchResultsHiddenByFilters';
import {useTopLevelQueryVariables} from './useTopLevelQueryVariables';

interface UseRunSelectorPropsOptions {
  analyticsLocation: RunSelectorAnalyticsLocation;
  customRunColorsRef: CustomRunColorsTypes.Ref;
  customRunNamesRef: CustomRunNamesTypes.Ref;
  isExpandable?: boolean;
  isExpanded?: boolean;
  runSetRef: RunSetTypes.Ref;
  selectedRunName?: string;
  showArtifactCounts?: boolean;
  tempSelectionsRef: TempSelectionsTypes.Ref;
}

function hasActiveRunGroupColor(
  customRunColors: CustomRunColorsTypes.CustomRunColors | undefined,
  grouping: RunTypes.Key[],
  runSetId: string
) {
  if (customRunColors == null || grouping.length === 0) {
    return false;
  }

  const groupingKeys = grouping.map(Run.keyToString);
  return getParsedRunColorGroupKeys(customRunColors).some(
    ({key, path, runsetId}) =>
      runsetId === runSetId &&
      path.length > 0 &&
      path.length <= grouping.length &&
      path.every((entry, index) => entry.key === groupingKeys[index]) &&
      Boolean(customRunColors[key]?.trim())
  );
}

const makeRunsTableSelector = (runSetRef: RunSetTypes.Ref) =>
  createSelector(makeRunSetSelector(runSetRef), runSet => ({
    filtersRef: runSet.filtersRef,
    groupSelectionsRef: runSet.groupSelectionsRef,
    id: runSet.id,
    name: runSet.name,
    project: runSet.project,
    runFeed: runSet.runFeed,
    runNameTruncationType: runSet.runNameTruncationType,
    search: runSet.search,
    sortRef: runSet.sortRef,
    baselineRunId: runSet.baselineRunId,
    pinnedRunIds: runSet.pinnedRunIds,
  }));

export function useRunSelectorProps(props: UseRunSelectorPropsOptions) {
  'use memo';
  const partialRunSet = useMemoizedSelector(
    makeRunsTableSelector,
    props.runSetRef
  );

  const customRunNames = ViewHooks.usePartMaybe(props.customRunNamesRef);
  const customRunColors = ViewHooks.usePartMaybe(props.customRunColorsRef);
  const groupSelections = ViewHooks.usePart(partialRunSet.groupSelectionsRef);
  const tempSelections = ViewHooks.usePart(props.tempSelectionsRef);
  const hasCheckedRunGroupColorWorkspaceLoadRef = useRef(false);

  useEffect(() => {
    if (hasCheckedRunGroupColorWorkspaceLoadRef.current) {
      return;
    }

    hasCheckedRunGroupColorWorkspaceLoadRef.current = true;
    if (
      !hasActiveRunGroupColor(
        customRunColors,
        groupSelections.grouping,
        props.runSetRef.id
      )
    ) {
      return;
    }

    trackRunColor({
      action: 'Workspace with run group coloring loaded',
      isGroupingEnabled: true,
      location: props.analyticsLocation,
    });
  }, [
    customRunColors,
    groupSelections.grouping,
    props.analyticsLocation,
    props.runSetRef.id,
  ]);

  const storedFilters = ViewHooks.usePart(partialRunSet.filtersRef);
  const runSetFilters = useMemo(
    () => getFiltersForQuery(storedFilters),
    [storedFilters]
  );
  const sort = ViewHooks.usePart(partialRunSet.sortRef);
  const truncationType =
    partialRunSet.runNameTruncationType ?? TruncationType.Middle;

  const toggleExpandedRow = ViewHooks.useViewAction(
    partialRunSet.groupSelectionsRef,
    GroupSelectionsActions.toggleExpandedRowAddress
  );

  const tempSelectAll = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectAll,
    props.tempSelectionsRef,
    props.runSetRef
  );

  const tempSelectNone = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectNone,
    props.tempSelectionsRef,
    props.runSetRef
  );

  const {entityName, projectName, projectInternalId} = useRunSetProject(
    partialRunSet.project
  );

  const groupTempSelection = useMemo(
    () => ({
      grouping: groupSelections.grouping,
      selections: tempSelections,
      expandedRowAddresses: groupSelections.expandedRowAddresses,
    }),
    [
      groupSelections.grouping,
      groupSelections.expandedRowAddresses,
      tempSelections,
    ]
  );

  const {mergeFilters} = useRunsQueryContext();
  const runSelectorQuery = useRunSelectorQuery(projectInternalId);
  const bulkEditorProjectTagsQuery = useBulkEditorProjectTagsQuery({
    entityName,
    projectName,
    filters: runSetFilters,
    mergeFilters,
    groupTempSelection,
    ...props,
  });
  const {tempSelectedCount, tempOutputArtifactsCount, tempSelectedActiveCount} =
    useSelectedCounts(
      props.runSetRef,
      props.tempSelectionsRef,
      props.customRunNamesRef
    );

  const [stopRun] = Generated.useStopRunMutation();
  const updateRun = RunHooks.useUpdateRun();
  const bulkUpdateRunTags = RunHooks.useBulkUpdateRunTags();
  const [moveRuns] = Generated.useMoveRunsMutation();
  const deleteRuns = RunHooks.useDeleteRuns();
  const [updateGroupRun] = Generated.useUpsertRunGroupMutation();

  const runSetUpdate = ViewHooks.useViewAction(
    props.runSetRef,
    RunSetActions.update
  );

  const setGrouping = ViewHooks.useViewAction(
    partialRunSet.groupSelectionsRef,
    GroupSelectionsActions.setGrouping
  );

  const setSort = ViewHooks.useViewAction(
    partialRunSet.sortRef,
    SortActions.set
  );

  const selectAll = ViewHooks.useViewAction(
    partialRunSet.groupSelectionsRef,
    GroupSelectionsActions.selectAll
  );

  const selectNone = ViewHooks.useViewAction(
    partialRunSet.groupSelectionsRef,
    GroupSelectionsActions.selectNone
  );

  const setProject = useCallback(
    (newEntityName: string, newProjectName: string, newProjectId: string) =>
      runSetUpdate({
        project: {
          entityName: newEntityName,
          name: newProjectName,
          id: newProjectId,
        },
      }),
    [runSetUpdate]
  );

  const setFilters = ViewHooks.useViewAction(
    partialRunSet.filtersRef,
    FilterActions.set
  );

  const setTruncationType = useCallback(
    (newTruncationType: TruncationType) =>
      runSetUpdate({runNameTruncationType: newTruncationType}),
    [runSetUpdate]
  );

  const setTableSettings = useCallback(
    (runFeed: TableSettings) => runSetUpdate({runFeed}),
    [runSetUpdate]
  );

  const defaultKeys = useDefaultKeys();
  const filteredKeys = useFilteredKeys(runSelectorQuery);

  const tableSettings = useDeepMemo(
    useMemo(() => {
      return TableCols.cleanRunFeedConfig(
        partialRunSet.runFeed || EMPTY_TABLE_SETTINGS,
        filteredKeys,
        defaultKeys
      );
    }, [defaultKeys, filteredKeys, partialRunSet.runFeed])
  );

  const [launchDrawerJobRun, setLaunchDrawerJobRun] =
    useState<LaunchDrawerJobRun | null>(null);
  const setLaunchDrawerRow = useCallback((name: string, jobInfo?: JobInfo) => {
    if (jobInfo == null) {
      return;
    }
    setLaunchDrawerJobRun({
      runName: name,
      jobId: jobInfo.id,
      jobVersion: jobInfo.versionStr,
    });
  }, []);

  const closeLaunchConfigModal = useCallback(() => {
    setLaunchDrawerJobRun(null);
  }, []);

  const launchAgentQuery = Generated.useEntityAgentsQuery({
    variables: {entityName},
  });
  const isLaunchActive = useMemo(() => {
    return (
      launchAgentQuery.data?.entity?.launchProject?.launchAgents?.some(
        agent => {
          return ['POLLING', 'RUNNING'].includes(agent.agentStatus);
        }
      ) ?? false
    );
  }, [launchAgentQuery]);

  const {hasPublicQueueAccess} = usePublicQueueAccess();

  const isTableCollapsed = (props.isExpandable && !props.isExpanded) ?? false;

  const useLeanGroupedRunsQuery = useLeanGroupedRunsQueryFlag({
    entityName,
    grouping: groupSelections.grouping,
    hasPinnedColumns: hasPinnedColumnsForLeanQuery(tableSettings.columnPinned),
    isTableCollapsed,
  });

  const topLevelQueryVariables = useTopLevelQueryVariables({
    customRunNames,
    enableArtifactCounts: props.showArtifactCounts ?? false,
    entityName,
    groupSelections,
    isTableCollapsed,
    mergeFilters,
    projectInternalId,
    projectName,
    runSetRef: props.runSetRef,
    runSetFilters,
    sort,
    tableSettings,
    useLeanGroupedRunsQuery,
  });

  const runSelectorRuns = useRunSelectorRuns({
    isRootQuery: true,
    queryId: props.runSetRef.id,
    pageSize: tableSettings.pageSize || EMPTY_TABLE_SETTINGS.pageSize,
    partialVariables: topLevelQueryVariables,
    shouldOnlyShowSelected: Boolean(tableSettings.onlyShowSelected),
    skip: runSelectorQuery.initialLoading,
    useLeanGroupedRunsQuery,
  });

  const {
    loadMoreRuns,
    isLoadingRuns,
    isLoadingRunsPolled,
    totalRuns,
    runFilterErrorMessage,
  } = runSelectorRuns;
  const runs = useMemo(() => {
    return getRows(runSelectorRuns.runs, props.selectedRunName, {
      id: partialRunSet.id,
      name: partialRunSet.name,
    });
  }, [
    props.selectedRunName,
    partialRunSet.id,
    partialRunSet.name,
    runSelectorRuns.runs,
  ]);
  const pinnedRuns = useMemo(() => {
    return getRows(runSelectorRuns.pinnedRuns, props.selectedRunName, {
      id: partialRunSet.id,
      name: partialRunSet.name,
    });
  }, [
    runSelectorRuns.pinnedRuns,
    props.selectedRunName,
    partialRunSet.id,
    partialRunSet.name,
  ]);
  const baselineRun = useMemo(() => {
    if (!partialRunSet.baselineRunId) {
      return null;
    }
    const baselineRun = pinnedRuns.find(
      run => run.id === partialRunSet.baselineRunId
    );
    return baselineRun ?? null;
  }, [pinnedRuns, partialRunSet.baselineRunId]);

  const combinedFilter = useCombinedFilter({
    userFilters: runSetFilters,
    groupSelection: groupTempSelection,
    mergeFilters,
    search: partialRunSet.search,
    customRunNames,
  });

  const hiddenByFiltersEnabled =
    useRampFlagRunsTableResultsHiddenByFiltersBanner();
  const {hasHiddenResults: showSearchResultsHiddenByFiltersBanner} =
    useSearchResultsHiddenByFilters({
      enabled: hiddenByFiltersEnabled,
      userFilters: runSetFilters,
      mergeFilters,
      groupSelections,
      onlyShowSelected: Boolean(tableSettings.onlyShowSelected),
      search: partialRunSet.search,
      customRunNames,
      projectInternalId,
    });

  return {
    baselineRun,
    bulkEditorProjectTagsQuery,
    bulkUpdateRunTags,
    closeLaunchConfigModal,
    combinedFilter,
    defaultKeys,
    deleteRuns,
    entityName,
    filteredKeys,
    filters: runSetFilters,
    filtersRef: partialRunSet.filtersRef,
    storedFilters,
    groupSelections,
    groupSelectionsRef: partialRunSet.groupSelectionsRef,
    groupTempSelection,
    showSearchResultsHiddenByFiltersBanner,
    isLaunchActive,
    isLaunchPublicQueueActive: hasPublicQueueAccess,
    isLoadingRuns,
    isLoadingRunsPolled,
    isTableCollapsed,
    launchDrawerJobRun,
    loadMoreRuns,
    moveRuns,
    pinnedRuns,
    projectName,
    runFilterErrorMessage,
    runs,
    runSelectorQuery,
    selectAll,
    selectNone,
    setFilters,
    setGrouping,
    setLaunchDrawerRow,
    setProject,
    setSort,
    setTableSettings,
    setTruncationType,
    sort,
    sortRef: partialRunSet.sortRef,
    stopRun,
    tableSettings,
    tempOutputArtifactsCount,
    tempSelectAll,
    tempSelectedActiveCount,
    tempSelectedCount,
    tempSelectionsRef: props.tempSelectionsRef,
    tempSelectNone,
    toggleExpandedRow,
    topLevelQueryVariables,
    totalRuns,
    truncationType,
    updateGroupRun,
    updateRun,
    useLeanGroupedRunsQuery,
  };
}

export type RunSelectorPropsHook = ReturnType<typeof useRunSelectorProps>;
